import { useMutation } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { StandardErrorToast } from 'design-system/src/components/Toast/Toast';
import { Toast } from 'design-system/src/components/Toast/Toast';
import { client, OutboundTaskTypeKey, OutboundTaskType, TaskComment, TaskEvent } from '../../../../api';
import type { Task, WithAdditionalTaskResponseFields } from '../../types';
import { TaskContext } from '../../contexts';
import { RenderHtmlString } from 'design-system/src/components/RenderHtmlString/RenderHtmlString';
import Button from 'design-system/src/components/Button/Button';
import ButtonRow, { Alignment } from 'design-system/src/components/ButtonRow/ButtonRow';
import DeclineModal from '../../DeclineModal';
import NewFollowupTaskModal from '../../NewFollowupTaskModal';
import { ActionCard } from '../../../ActionCard';
import { OtherRequestButton } from '../../actions/shared/OtherRequestButton';
import { sponsoredTaskCopy } from '../../../businessRelationshipNotices/copy';
import { tasksQueryKey } from '../../../../queries';
import { useQueryClient } from '@tanstack/react-query';
import { TaskInstructionSummary, TaskCompletionEventBanner } from '../../shared';
import PharmacySearch from '../../../PharmacySearch/PharmacySearch';
import { StructuredTaskViewWrapper } from '../StructuredTaskViewWrapper';

/**
 * Dedicated task view for Select patient's preferred pharmacy (PHARMACY_REQUESTED).
 */
const PharmacyRequestedTaskView: React.FC<{
  taskResponse: WithAdditionalTaskResponseFields<Task>;
}> = ({ taskResponse }) => {
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [showNewFollowupTaskModal, setShowNewFollowupTaskModal] = useState(false);
  const [hasSelection, setHasSelection] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitTrigger, setSubmitTrigger] = useState(0);

  const { taskId, onTaskUpdate } = useContext(TaskContext);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isTrainingMode = import.meta.env.VITE_TRAINING_MODE === 'true';

  const { drug, patient, prescriber } = taskResponse;
  const task = taskResponse.task;
  const isCompleted = task.status === 'completed';
  const rxId = drug.id;

  const handleTaskResponse = useMutation({
    mutationFn: async (response: string) => {
      if (isTrainingMode) {
        console.warn(`Training Mode: Mocking task response "${response}" for task ID: ${taskId}`);
        return;
      }
      const formData = new FormData();
      formData.append('task_response', response);
      await client.post(`tasks/${taskId}/respond`, formData);
    },
    onSuccess: () => {
      onTaskUpdate();
      toast.custom(() => <Toast variant="success">Task response received.</Toast>);
      navigate('/tasks');
      queryClient.invalidateQueries({ queryKey: tasksQueryKey() });
    },
    onError: (error) => {
      toast.custom(() => <StandardErrorToast />);
      console.error(error);
      Sentry.captureException(error);
    },
  });

  const initialComment =
    task.timeline && task.timeline.length > 0 && task.timeline[0].item_type === 'comment'
      ? (task.timeline[0] as TaskComment)
      : undefined;

  // Get completion event for completed tasks
  let completionEvent: TaskEvent | undefined;
  if (isCompleted) {
    if (task.timeline && task.timeline.length > 1) {
      const lastTimelineItem = task.timeline[task.timeline.length - 1];
      if (lastTimelineItem?.item_type === 'event') {
        completionEvent = lastTimelineItem as TaskEvent;
      }
    }
    if (!completionEvent) {
      Sentry.captureMessage('No completion event found for Pharmacy Requested task', {
        level: 'error',
        extra: {
          taskId,
        },
      });
    }
  }

  const handlePharmacySubmit = () => {
    handleTaskResponse.mutate('select_preferred_pharmacy');
  };

  const handleContinue = () => {
    setSubmitTrigger((prev) => prev + 1);
  };

  return (
    <>
      <StructuredTaskViewWrapper
        taskType={(task.task_title || task.type) as OutboundTaskType}
        generalInfoTableProps={{
          patientName: drug.patient_name,
          prescriberName: drug.prescriber_name,
          dob: drug.dob,
          prescribedOn: drug.prescription_recieved_at,
          drugName: drug.name,
          prescriptionId: drug.id,
        }}
        taskCreatedAt={task.created_at}
        isCompleted={isCompleted}
        showBusinessRelationshipSmallPrint
        sidebar={{
          drug: {
            data: drug,
            showBusinessRelationshipNotice: task.is_pharma_contract_task,
            businessRelationshipNoticeCopy: sponsoredTaskCopy,
          },
          patient: patient ?? undefined,
          prescriber: prescriber ?? undefined,
        }}
      >
        {isCompleted && <TaskCompletionEventBanner completionEvent={completionEvent} />}

        <div className={`mb-6 flex flex-col gap-4 ${isCompleted ? 'border-b-grayscale-100 pb-4 border-b-solid' : ''}`}>
          <TaskInstructionSummary>Select the patient's preferred pharmacy:</TaskInstructionSummary>
          {initialComment && (
            <Typography styledAs="bodyExtraSmallDMSans">
              <RenderHtmlString>{initialComment.message}</RenderHtmlString>
            </Typography>
          )}
        </div>

        {!isCompleted && (
          <ActionCard>
            <div className="mb-2 w-full">
              <PharmacySearch
                rxId={rxId}
                onSubmit={handlePharmacySubmit}
                onSelectionChange={setHasSelection}
                onSubmittingChange={setIsSubmitting}
                submitTrigger={submitTrigger}
              />
            </div>
            <ButtonRow align={Alignment.SpaceBetween} fullWidth className="border-t-grayscale-100 pt-4 border-t-solid">
              <ButtonRow align={Alignment.Left}>
                <Button
                  variant="tertiary"
                  size="sm"
                  onClick={() => setShowDeclineModal(true)}
                  disabled={isSubmitting || handleTaskResponse.isPending}
                >
                  Cancel prescription
                </Button>
                <OtherRequestButton setShowNewFollowupTaskModal={setShowNewFollowupTaskModal} />
              </ButtonRow>
              <Button
                size="sm"
                onClick={handleContinue}
                disabled={!hasSelection || isSubmitting || handleTaskResponse.isPending}
                isLoading={isSubmitting || handleTaskResponse.isPending}
              >
                Submit
              </Button>
            </ButtonRow>
          </ActionCard>
        )}
      </StructuredTaskViewWrapper>

      {showDeclineModal && (
        <DeclineModal
          closeModal={() => setShowDeclineModal(false)}
          patient={patient}
          drug={drug}
          taskKey={task.task_key as OutboundTaskTypeKey}
          taskId={taskId}
          onTaskUpdate={onTaskUpdate}
        />
      )}

      {showNewFollowupTaskModal && (
        <NewFollowupTaskModal
          closeModal={() => setShowNewFollowupTaskModal(false)}
          patient={patient}
          drug={drug}
          patientId={patient.id}
          prescriptionId={drug.id}
          taskId={taskId}
          relatedTaskKey={task.task_key as OutboundTaskTypeKey}
          relatedTaskType={task.type as OutboundTaskType}
        />
      )}
    </>
  );
};

export default PharmacyRequestedTaskView;
